import type { NotificationLevel, NotificationType } from '@ad-manager/shared';
import { apiClient } from './client';
import type { Paginated } from './ad-accounts';

export type { NotificationType, NotificationLevel };

export interface NotificationRow {
  id: number;
  user_id: number;
  type: NotificationType;
  level: NotificationLevel;
  title: string;
  body: string | null;
  related_token_id: number | null;
  related_ad_account_id: number | null;
  metadata: Record<string, unknown> | null;
  read_at: string | null;
  created_at: string;
}

export interface NotificationListQuery {
  type?: NotificationType;
  level?: NotificationLevel;
  unread_only?: boolean;
  page?: number;
  per_page?: number;
}

export async function listNotifications(q: NotificationListQuery = {}): Promise<Paginated<NotificationRow>> {
  const { data } = await apiClient.get<Paginated<NotificationRow>>('/notifications', {
    params: q,
  });
  return data;
}

export async function getUnreadCount(): Promise<number> {
  const { data } = await apiClient.get<{ count: number }>('/notifications/unread-count');
  return data.count;
}

export async function markRead(id: number): Promise<void> {
  await apiClient.post(`/notifications/${id}/read`);
}

export async function markAllRead(): Promise<void> {
  await apiClient.post('/notifications/mark-all-read');
}

export async function deleteNotification(id: number): Promise<void> {
  await apiClient.delete(`/notifications/${id}`);
}

// ---------------------------------------------------------------------------
// Notification channels — IM fan-out (飞书 / 钉钉 / 企业微信 / 自定义 webhook)
// ---------------------------------------------------------------------------

export type NotificationChannelType = 'feishu' | 'dingtalk' | 'wecom' | 'webhook';

export interface NotificationChannelPublic {
  id: number;
  type: NotificationChannelType;
  name: string;
  webhook_url: string;
  /** Secret is never returned — this flags whether one is stored. */
  secret_set: boolean;
  sign_enabled: boolean;
  event_types: NotificationType[] | null;
  min_level: NotificationLevel;
  is_active: boolean;
  last_delivered_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateNotificationChannelInput {
  type: NotificationChannelType;
  name: string;
  webhook_url: string;
  secret?: string;
  sign_enabled?: boolean;
  event_types?: NotificationType[] | null;
  min_level?: NotificationLevel;
}

export interface UpdateNotificationChannelInput {
  name?: string;
  webhook_url?: string;
  secret?: string | null;
  sign_enabled?: boolean;
  event_types?: NotificationType[] | null;
  min_level?: NotificationLevel;
  is_active?: boolean;
}

export async function listChannels(): Promise<NotificationChannelPublic[]> {
  const { data } = await apiClient.get<NotificationChannelPublic[]>('/notifications/channels');
  return data;
}

export async function createChannel(
  input: CreateNotificationChannelInput,
): Promise<NotificationChannelPublic> {
  const { data } = await apiClient.post<NotificationChannelPublic>(
    '/notifications/channels',
    input,
  );
  return data;
}

export async function updateChannel(
  id: number,
  input: UpdateNotificationChannelInput,
): Promise<void> {
  await apiClient.patch(`/notifications/channels/${id}`, input);
}

export async function deleteChannel(id: number): Promise<void> {
  await apiClient.delete(`/notifications/channels/${id}`);
}

export async function testChannel(
  id: number,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { data } = await apiClient.post<{ ok: true } | { ok: false; error: string }>(
    `/notifications/channels/${id}/test`,
  );
  return data;
}
